import React from 'react';
import { isNullOperator, rawExpressionToFilter, SortDir } from '@fabric-ui/components';
import { flex } from '@fabric-ui/theme';
import { Box, Button } from '@mui/material';
import { isAdvancedFilterMode } from '@utils/filterUtils';

interface FilterActionButtonsProps {
  setErrors: React.Dispatch<React.SetStateAction<any[]>>;
  setConditionValue: React.Dispatch<React.SetStateAction<any>>;
  operator: 'and' | 'or';
  initialFilter: any;
  setOpen: (open: boolean) => void;
  onChange: (field: any, value: any) => void;
  field: string;
  onFilterChange: ((filter: any) => void) | undefined;
  conditionValue: any;
  getErrors: (val: any, operator: string) => string[] | undefined;
  mode: string;
  advancedFilterValue: { dir?: SortDir; top?: number; value?: string; type?: string };
  setAdvancedFilterValue: React.Dispatch<React.SetStateAction<{ dir?: SortDir; top?: number; value?: string; type?: string }>>;
}

function FilterActionButtons({
  setErrors,
  setConditionValue,
  operator,
  initialFilter,
  setOpen,
  onChange,
  field,
  onFilterChange,
  conditionValue,
  getErrors,
  mode,
  advancedFilterValue,
  setAdvancedFilterValue,
}: FilterActionButtonsProps) {
  const isAdvancedFilterComplete = React.useMemo(() => {
    if (!isAdvancedFilterMode(mode)) {
      return true; // Not in advanced mode, so this check doesn't apply
    }
    const { dir, top, value } = advancedFilterValue;
    return dir !== undefined && top !== undefined && top !== null && value !== undefined;
  }, [mode, advancedFilterValue]);

  const onApply = () => {
    if (isAdvancedFilterMode(mode)) {
      onChange(field, [encodeURIComponent(JSON.stringify(advancedFilterValue))]);
      onFilterChange?.(rawExpressionToFilter(field, advancedFilterValue));
      setOpen(false);
    } else {
      const validOperands = conditionValue[operator].filter(
        ({ value, operator }: { value: any; operator: string }) => typeof value === 'number' || isNullOperator(operator) || !!value
      );

      if (!validOperands.length) {
        setConditionValue({ [operator]: [initialFilter] });
        onChange(field, []);
        onFilterChange?.(rawExpressionToFilter(field, conditionValue));
        setOpen(false);
      } else {
        const errorsExp = validOperands.map(({ value, operator }: { value: any; operator: string }) => ({
          operator,
          value: getErrors(value, operator),
        }));
        if (errorsExp.some(({ value }) => value)) {
          setErrors(errorsExp);
        } else {
          const nextConditionValue = { [operator]: validOperands };
          setConditionValue(nextConditionValue);
          onChange(field, [encodeURIComponent(JSON.stringify(nextConditionValue))]);
          onFilterChange?.(rawExpressionToFilter(field, nextConditionValue));
          setOpen(false);
        }
      }
    }
  };

  return (
    <Box sx={{ p: 2, ...flex.justifyBetweenCenter }}>
      <Button
        onClick={() => {
          if (isAdvancedFilterMode(mode)) {
            setAdvancedFilterValue(initialFilter);
          }
          setErrors([]);
          setConditionValue({ [operator]: [initialFilter] });
        }}
        size="small">
        Reset
      </Button>
      <div>
        <Button onClick={() => setOpen(false)} size="small">
          Close
        </Button>
        <Button variant="contained" size="small" disabled={isAdvancedFilterMode(mode) && !isAdvancedFilterComplete} onClick={onApply}>
          Apply
        </Button>
      </div>
    </Box>
  );
}

export default FilterActionButtons;
